import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Fingerprint, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { registerPasskeyWithInvite, passkeySupported } from '../utils/webauthnHr';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegistraTelefono() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('Link non valido'); return; }
    fetch(`${API}/hr/invite/${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Link non valido');
        setInfo(d);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const register = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await registerPasskeyWithInvite(token);
      localStorage.setItem('hw_token', res.access_token);
      localStorage.setItem('hw_user', JSON.stringify(res.user));
      setDone(true);
      setTimeout(() => { window.location.href = '/timbratura'; }, 1800);
    } catch (e) {
      setError(e.message || 'Registrazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
          {done ? <CheckCircle2 className="h-9 w-9 text-emerald-400" /> : <Fingerprint className="h-9 w-9 text-emerald-400" />}
        </div>

        {error && !info ? (
          <>
            <h1 className="text-xl font-semibold text-white mb-2">Ops!</h1>
            <p className="text-slate-400 text-sm" data-testid="invite-error">{error}</p>
          </>
        ) : done ? (
          <>
            <h1 className="text-xl font-semibold text-white mb-2">Telefono registrato! 🎉</h1>
            <p className="text-slate-400 text-sm">Ti sto portando alla pagina Timbratura…</p>
          </>
        ) : !info ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 text-slate-500 animate-spin" /></div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-white mb-2" data-testid="invite-welcome">Ciao, {info.name}! 👋</h1>
            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
              Benvenuto/a nel sistema timbrature di Holiday World.<br />
              Registra <b className="text-slate-200">questo telefono</b> con la tua impronta o il tuo volto:
              da oggi timbrerai così, <b className="text-slate-200">senza password</b>.
            </p>
            <Button
              data-testid="invite-register-btn"
              onClick={register}
              disabled={busy || !passkeySupported()}
              className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-500"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Fingerprint className="h-5 w-5 mr-2" />}
              Registra questo telefono
            </Button>
            {!passkeySupported() && (
              <p className="text-amber-400 text-xs mt-3 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Questo browser non supporta le passkey: apri il link da Chrome o Safari sul telefono
              </p>
            )}
            {error && (
              <p className="text-rose-400 text-xs mt-3" data-testid="invite-register-error">{error}</p>
            )}
            <p className="text-slate-600 text-[11px] mt-6">
              Il link è personale e monouso. Problemi? Contatta l'amministrazione.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
